// v5.77 — Creative Edit controls, rendered in the right SidebarPanel while a
// tile is in in-place edit mode. Light-themed to match the panel chrome.
// Closing the panel (unmount) exits edit mode on the tile.
import { useEffect } from 'react';
import { Plus, Trash2, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Check, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { EDITOR_FONTS, ensureGoogleFont, useCreativeEditStore } from './creative-edit-store';
import { useCanvasStore } from './canvas-store';

export function CreativeEditPanel() {
  const activeImageId = useCreativeEditStore((s) => s.activeImageId);
  const doc = useCreativeEditStore((s) => (s.activeImageId ? s.prepares[s.activeImageId]?.doc ?? null : null));
  const phase = useCreativeEditStore((s) => (s.activeImageId ? s.prepares[s.activeImageId]?.phase ?? 'preparing' : 'preparing'));
  const preparingOthers = useCreativeEditStore((s) =>
    Object.entries(s.prepares).filter(([id, p]) => p.phase === 'preparing' && id !== s.activeImageId).length);
  const selectedTextId = useCreativeEditStore((s) => s.selectedTextId);
  const saving = useCreativeEditStore((s) => s.saving);
  const convertingIds = useCreativeEditStore((s) => s.convertingIds);
  const { updateText, addText, deleteText, setSelected, saveToCanvas, convertBlock, close } = useCreativeEditStore.getState();
  const isEditedCopy = useCreativeEditStore((s) => !!(s.activeImageId && (s.prepares[s.activeImageId]?.sourceUrl ?? '').includes('/creative-edit/exports/')));
  const { toast } = useToast();

  // Panel unmount = user closed the panel (or switched panels) → exit edit
  // mode so the tile goes back to its normal image.
  useEffect(() => () => { useCreativeEditStore.getState().close(); }, []);

  const selected = doc?.texts.find((t) => t.id === selectedTextId) ?? null;
  const fontStyleHas = (style: string, flag: 'bold' | 'italic') => (flag === 'bold' ? /bold|800|900/.test(style) : style.includes(flag));
  const toggleStyle = (flag: 'bold' | 'italic') => {
    if (!selected) return;
    const parts = new Set(selected.fontStyle.split(' ').filter((p) => p && p !== 'normal'));
    if (parts.has(flag)) parts.delete(flag); else parts.add(flag);
    updateText(selected.id, { fontStyle: parts.size ? Array.from(parts).join(' ') : 'normal' });
  };

  const handleSave = async () => {
    const result = await saveToCanvas();
    if (result === 'updated') {
      // Editing a copy: the tile updated in place — keep working.
      toast({ title: 'Changes saved', description: 'This creative was updated in place.' });
    } else if (result === 'copy') {
      toast({ title: 'Saved to canvas', description: 'Edited copy placed next to the original.' });
      close();
      useCanvasStore.getState().closeSidebarPanel();
    } else {
      toast({ title: 'Save failed', description: result, variant: 'destructive' });
    }
  };

  const inputStyle: React.CSSProperties = {
    background: '#FFFFFF', border: '1px solid #E5E2DC', borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: '#1A1A1A', outline: 'none', width: '100%',
  };
  const chipBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
    border: '1px solid ' + (active ? '#6366F1' : '#E5E2DC'),
    background: active ? 'rgba(99,102,241,0.12)' : '#FFFFFF',
    color: active ? '#6366F1' : '#6B7280',
  });

  if (phase !== 'ready' || !doc || !activeImageId) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: '#8A8377', fontSize: 13 }}>
        {phase === 'failed' ? 'Preparing the editor failed — close and retry.' : 'Preparing text layers on the tile…'}
        {preparingOthers > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#B5B0A8' }}>
            {preparingOthers} more creative{preparingOthers === 1 ? '' : 's'} preparing in the background.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="creative-edit-save"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '9px 12px', borderRadius: 9, border: 'none', cursor: saving ? 'wait' : 'pointer',
          background: '#1A1A1A', color: '#fff', fontSize: 13, fontWeight: 600, opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? <Loader2 size={14} style={{ animation: 'spin 0.9s linear infinite' }} /> : <Check size={14} />}
        {isEditedCopy ? 'Save changes' : 'Save to canvas'}
      </button>

      <button
        onClick={addText}
        data-testid="creative-edit-add-text"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '8px 12px', borderRadius: 9, border: '1px solid #E5E2DC', cursor: 'pointer',
          background: '#FFFFFF', color: '#1A1A1A', fontSize: 13, fontWeight: 500,
        }}
      >
        <Plus size={14} /> Add text
      </button>

      {selected && !selected.struck ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8A8377', fontWeight: 600 }}>Selected text</div>
          <div style={{ fontSize: 12, color: '#1A1A1A', background: '#F5F3EE', borderRadius: 8, padding: '8px 10px', whiteSpace: 'pre-wrap' }}>{selected.text}</div>
          <button
            onClick={async () => {
              const err = await convertBlock(selected.id);
              if (err) toast({ title: 'Could not make editable', description: err, variant: 'destructive' });
            }}
            disabled={convertingIds.includes(selected.id)}
            data-testid="creative-edit-make-editable"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '9px 12px', borderRadius: 9, border: 'none',
              cursor: convertingIds.includes(selected.id) ? 'wait' : 'pointer',
              background: '#1A1A1A', color: '#fff', fontSize: 13, fontWeight: 600,
              opacity: convertingIds.includes(selected.id) ? 0.6 : 1,
            }}
          >
            {convertingIds.includes(selected.id) ? <Loader2 size={14} style={{ animation: 'spin 0.9s linear infinite' }} /> : null}
            {convertingIds.includes(selected.id) ? 'Making editable…' : 'Make editable'}
          </button>
          <div style={{ fontSize: 11, color: '#8A8377', lineHeight: 1.6 }}>
            This text is still the original image — pixel identical. Making it editable erases it from the image (~10s) and replaces it with a matching editable layer. Everything else stays untouched.
          </div>
        </div>
      ) : selected ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8A8377', fontWeight: 600 }}>Selected text</div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#6B7280' }}>Font</span>
            <select
              value={selected.fontFamily}
              onChange={(e) => {
                ensureGoogleFont(e.target.value);
                updateText(selected.id, { fontFamily: e.target.value });
                document.fonts.load(`400 24px "${e.target.value}"`).catch(() => {});
              }}
              style={{ ...inputStyle, fontFamily: `"${selected.fontFamily}", sans-serif` }}
            >
              {EDITOR_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>

          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>Size</span>
              <input
                type="number"
                min={8}
                value={selected.fontSize}
                onChange={(e) => updateText(selected.id, { fontSize: Math.max(8, Number(e.target.value) || 8) })}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>Color</span>
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(selected.fill) ? selected.fill : '#ffffff'}
                onChange={(e) => updateText(selected.id, { fill: e.target.value })}
                style={{ width: 44, height: 34, border: '1px solid #E5E2DC', borderRadius: 8, background: '#fff', cursor: 'pointer', padding: 2 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>Highlight</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="color"
                  value={selected.bgFill && /^#[0-9a-f]{6}$/i.test(selected.bgFill) ? selected.bgFill : '#ffe63b'}
                  onChange={(e) => updateText(selected.id, { bgFill: e.target.value })}
                  style={{ width: 44, height: 34, border: '1px solid #E5E2DC', borderRadius: 8, background: '#fff', cursor: 'pointer', padding: 2, opacity: selected.bgFill ? 1 : 0.45 }}
                />
                {selected.bgFill && (
                  <button
                    title="Remove highlight"
                    onClick={() => updateText(selected.id, { bgFill: undefined })}
                    style={{ border: 'none', background: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 13, padding: 2 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => toggleStyle('bold')} title="Bold" style={chipBtn(fontStyleHas(selected.fontStyle, 'bold'))}><Bold size={14} /></button>
            <button onClick={() => toggleStyle('italic')} title="Italic" style={chipBtn(fontStyleHas(selected.fontStyle, 'italic'))}><Italic size={14} /></button>
            <div style={{ width: 1, height: 18, background: '#E5E2DC', margin: '0 3px' }} />
            {(['left', 'center', 'right'] as const).map((a) => {
              const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight;
              return <button key={a} onClick={() => updateText(selected.id, { align: a })} title={`Align ${a}`} style={chipBtn(selected.align === a)}><Icon size={14} /></button>;
            })}
            <div style={{ flex: 1 }} />
            <button onClick={() => deleteText(selected.id)} title="Delete text" style={{ ...chipBtn(false), color: '#EF4444' }}><Trash2 size={14} /></button>
          </div>

          <div style={{ fontSize: 11, color: '#8A8377', lineHeight: 1.6 }}>
            Double-click the text on the tile to retype it. Drag to move, side handles to resize the box. Zoom the canvas for fine control.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8A8377', fontWeight: 600, marginBottom: 2 }}>Text layers</div>
          {doc.texts.length === 0 && <div style={{ fontSize: 12, color: '#8A8377' }}>No text found on this creative. Use "Add text" to create some.</div>}
          {doc.texts.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                textAlign: 'left', padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E2DC',
                background: '#FFFFFF', fontSize: 12, color: '#1A1A1A', cursor: 'pointer',
                overflow: 'hidden', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: t.struck ? `"${t.fontFamily}", sans-serif` : undefined }}>
                {t.text.split('\n')[0] || '(empty)'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: t.struck ? '#6366F1' : '#8A8377', flexShrink: 0 }}>
                {convertingIds.includes(t.id) ? '…' : t.struck ? 'editable' : 'original'}
              </span>
            </button>
          ))}
        </div>
      )}
      {preparingOthers > 0 && (
        <div style={{ fontSize: 11, color: '#8A8377', borderTop: '1px solid #EFEDE8', paddingTop: 10 }}>
          {preparingOthers} more creative{preparingOthers === 1 ? '' : 's'} preparing in the background — click their tile when done.
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
